import { A } from "@/atoms-react/a/A.react";
import Column from "@/atoms-react/column/Column.react";
import CodeInput from "@/atoms-react/input/CodeInput.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import { Base } from "@/atoms-react/text/Text.react";
import useCheckEmailMutation from "@/features/flow/hooks/use-check-email";
import useValidateEmailMutation from "@/features/flow/hooks/use-validate-email";
import type { CheckEmailResponse, ConfirmationResponseData } from "@/features/register/types";
import { UserType } from "@/features/user/types";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { FlowHeader } from "../../../flow-header.react";
import useCaptchaVerify from "@/features/utils/hooks/use_captcha_verify";
import Loader from "@/atoms-react/loader/Loader.react";
import { validateEmailFormat } from "@/features/utils/functions";
import { useSearchParams } from "react-router-dom";

interface VerifyEmailRootProps extends LayoutBaseProps {
  input: VerifyEmailInput;
  onValidated?: (data?: UserType) => void;
  overlayMode?: boolean;
  mirror_uuid: string | null;
}

export type VerifyEmailInput = {
  email: string;
};

function VerifyEmail({ input, mirror_uuid, onValidated = () => {} }: VerifyEmailRootProps) {
  const { t } = useTranslation(["pass"]);
  const [codeValidation, setCodeValidation] = useState<CheckEmailResponse | null>(null);
  const [canResendCode, setCanResendCode] = useState(false);
  const [focusError, setFocusError] = useState(false);
  const { mutateAsync: sendCode, isPending: isPendingSendCode } = useCheckEmailMutation();
  const { mutate: validateEmail, isPending: isPendingValidateEmail } = useValidateEmailMutation();
  const { isReady } = useCaptchaVerify();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email");
  const mirrorUuid = mirror_uuid ?? searchParams.get("mirror_uuid");

  useEffect(() => {
    if ((mirrorUuid && emailParam && isReady) || (!mirrorUuid && isReady)) execute();
  }, [mirrorUuid, isReady, emailParam]);
  const execute = () => {
    let email = input.email;
    if (!email || email == "") {
      email = emailParam ?? "";
    }
    if (validateEmailFormat(email)) {
      sendCode(email, {
        onSuccess: (response) => {
          setCodeValidation(response);
          setCanResendCode(false);
        },
        onError: (error) => {
          if (error.message === t("flow.id_control.error.check_email"))
            toast.error(t("flow.id_control.signup.error.error_sending"));
          else toast.error(error.message);
        }
      });
      setTimeout(() => setCanResendCode(true), 5000);
    }
  };
  if (!isReady && mirrorUuid)
    return (
      <Column>
        <FlowHeader
          title={t("flow.id_control.signup.label.verify_email_title")}
          description={
            <>
              {t("flow.id_control.signup.label.verify_email_description_1")}{" "}
              <Base className={"inline font-semibold"}>{input?.email ?? ""}</Base>{" "}
              {t("flow.id_control.signup.label.verify_email_description_2")}
            </>
          }
        />
        <Loader className={"mb-6"} />
      </Column>
    );
  return (
    <>
      <Column>
        <FlowHeader
          title={t("flow.id_control.signup.label.verify_email_title")}
          description={
            <>
              {t("flow.id_control.signup.label.verify_email_description_1")}{" "}
              <Base className={"inline font-semibold"}>{input?.email ?? ""}</Base>{" "}
              {t("flow.id_control.signup.label.verify_email_description_2")}
            </>
          }
        />
        <CodeInput
          codeNumberOfCharacters={8}
          className={"mb-6"}
          focus={focusError ? false : true}
          onChange={(code) =>
            validateEmail(
              {
                code,
                askProfile: mirrorUuid || sessionStorage.getItem("temp_uuid_real") ? true : false,
                cypher: codeValidation?.data.cypher ?? "",
                email: input.email
              },
              {
                onSuccess: (data: ConfirmationResponseData<UserType>) =>
                  onValidated(data.data ?? undefined),
                onError: (error) => {
                  setFocusError(true);
                  if (error.message === t("flow.id_control.signup.error.validate_error"))
                    toast.error(t("flow.id_control.signup.error.verify_email_wrong"));
                  else toast.error(error.message);
                }
              }
            )
          }
          loading={isPendingValidateEmail}
        />

        <A
          disabled={!canResendCode || isPendingSendCode || isPendingValidateEmail}
          className={twMerge(
            "font-medium text-primary transition-all",
            isPendingSendCode || !canResendCode || isPendingValidateEmail ? "opacity-0" : ""
          )}
          onClick={() => execute()}
        >
          {t("flow.id_control.signup.actions.resend_code")}
        </A>
      </Column>
    </>
  );
}

export default VerifyEmail;
